import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  User,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  deleteUser
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase';
import { emailService } from './emailService';

// Initialize Cloud Functions
const functions = getFunctions();

export interface AuthError {
  code: string;
  message: string;
}

export const authService = {
  // Sign up with email and password
  signUp: async (
    email: string, 
    password: string, 
    displayName: string, 
    dateOfBirth?: string, 
    legalAgreements?: {
      termsOfService: boolean;
      privacyPolicy: boolean;
      refundPolicy: boolean;
    }
  ): Promise<User> => {
    try {
      // ⚡ OPTIMIZED: Create account immediately without pre-validation
      // Backend security rules will handle validation
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update display name
      await updateProfile(user, { displayName });
      
      // Store additional signup data for profile creation
      if (dateOfBirth || legalAgreements) {
        const signupData = {
          dateOfBirth,
          legalAgreements: legalAgreements ? {
            termsOfService: {
              accepted: legalAgreements.termsOfService,
              acceptedAt: Date.now(),
              version: '1.0' // Current version
            },
            privacyPolicy: {
              accepted: legalAgreements.privacyPolicy,
              acceptedAt: Date.now(),
              version: '1.0' // Current version
            },
            refundPolicy: {
              accepted: legalAgreements.refundPolicy,
              acceptedAt: Date.now(),
              version: '1.0' // Current version
            }
          } : undefined
        };
        localStorage.setItem(`signup_data_${user.uid}`, JSON.stringify(signupData));
      }
      
      // ⚡ OPTIMIZED: Send email verification asynchronously (don't wait)
      // This doesn't block signup completion
      emailService.sendEmailVerification(user)
        .then(() => console.log('📧 Verification email sent'))
        .catch((emailError) => console.warn('⚠️ Failed to send verification email:', emailError));
      
      return user;
    } catch (error: any) {
      // If it's already an AuthError we threw, re-throw it
      if (error.code && error.message) {
        throw error;
      }
      
      // Otherwise, wrap Firebase auth error
      const authError: AuthError = {
        code: error.code,
        message: authService.getErrorMessage(error.code)
      };
      throw authError;
    }
  },

  // Sign in with email and password
  signIn: async (email: string, password: string): Promise<User> => {
    try {
      // ⚡ OPTIMIZED: Try sign-in immediately without pre-checking rate limit
      // Only check rate limit if sign-in fails (reduces 1 Cloud Function call)
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Sign-in successful');
      return userCredential.user;
      
    } catch (error: any) {
      // If already thrown a rate limit error, re-throw it
      if (error.code === 'auth/too-many-requests') {
        throw error;
      }
      
      // Sign-in failed - increment rate limit counter
      console.log('❌ Sign-in failed, recording failed attempt...');
      
      try {
        const signInRateLimiter = httpsCallable(functions, 'signInRateLimiter');
        const rateLimitResult = await signInRateLimiter({ email });
        
        const data = rateLimitResult.data as any;
        
        if (data.success && data.data && !data.data.allowed) {
          // NOW RATE LIMITED (just hit the limit)!
          const blockedUntil = data.data.blockedUntil;
          const minutesRemaining = blockedUntil 
            ? Math.ceil((blockedUntil - Date.now()) / 60000) 
            : 15;
          
          const authError: AuthError = {
            code: 'auth/too-many-requests',
            message: `⚠️ Too many failed sign-in attempts!\n\nYour account has been temporarily blocked for security.\n\nPlease wait ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} before trying again.`
          };
          throw authError;
        }
        
        // Show warning if close to limit (only for failed attempts)
        if (data.success && data.data && data.data.attemptsRemaining !== undefined) {
          const remaining = data.data.attemptsRemaining;
          if (remaining <= 2 && remaining > 0) {
            console.warn(`⚠️ Warning: ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining before rate limit!`);
          }
        }
      } catch (rateLimitError: any) {
        // If rate limit check itself failed, continue with original error
        if (rateLimitError.code === 'auth/too-many-requests') {
          throw rateLimitError;
        }
        console.warn('Rate limit check failed:', rateLimitError);
      }
      
      // Throw original sign-in error
      const authError: AuthError = {
        code: error.code,
        message: authService.getErrorMessage(error.code)
      };
      throw authError;
    }
  },

  // Sign in with Google
  signInWithGoogle: async (): Promise<User> => {
    try {
      const provider = new GoogleAuthProvider();

      // Add additional configuration to handle extension interference
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      const userCredential = await signInWithPopup(auth, provider);
      return userCredential.user;
    } catch (error: any) {
      const authError: AuthError = {
        code: error.code,
        message: authService.getErrorMessage(error.code)
      };
      throw authError;
    }
  },

  // Sign out
  signOut: async (): Promise<void> => {
    try {
      await signOut(auth);
    } catch (error: any) {
      const authError: AuthError = {
        code: error.code,
        message: authService.getErrorMessage(error.code)
      };
      throw authError;
    }
  },

  // Send password reset email (via MailerSend)
  resetPassword: async (email: string): Promise<void> => {
    try {
      await emailService.sendPasswordResetEmail(email);
    } catch (error: any) {
      const authError: AuthError = {
        code: error.code,
        message: authService.getErrorMessage(error.code)
      };
      throw authError;
    }
  },

  // Resend email verification
  resendEmailVerification: async (): Promise<void> => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user logged in');
      }
      await emailService.sendEmailVerification(user);
    } catch (error: any) {
      const authError: AuthError = {
        code: error.code,
        message: authService.getErrorMessage(error.code)
      };
      throw authError;
    }
  },

  // Change password
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No user logged in');
    }

    // Re-authenticate user with current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    
    try {
      // Re-authenticate
      await reauthenticateWithCredential(user, credential);
      
      // Update to new password
      await updatePassword(user, newPassword);
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        throw new Error('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('New password is too weak. Please use at least 6 characters');
      }
      throw error;
    }
  },

  // Delete user account
  deleteAccount: async (password?: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      // Check if user needs re-authentication
      // For OAuth users (Google), we need to use reauthenticateWithPopup
      // For email/password users, we need reauthenticateWithCredential
      const providerId = user.providerData[0]?.providerId;
      
      if (providerId === 'google.com') {
        // Google OAuth user - use popup re-authentication
        const { GoogleAuthProvider, reauthenticateWithPopup } = await import('firebase/auth');
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(user, provider);
      } else if (providerId === 'password') {
        // Email/password user - reauthenticate with password
        if (!password) {
          throw new Error('Password is required');
        }
        
        if (!user.email) {
          throw new Error('No email found for user');
        }
        
        // Reauthenticate with password
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
      }
      
      // Delete ALL user data from Firestore collections
      const { doc, deleteDoc, collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      console.log('🗑️ Starting user data deletion...');
      
      try {
        // 1. Delete main user profile
        const userDocRef = doc(db, 'users', user.uid);
        await deleteDoc(userDocRef);
        console.log('✅ User profile deleted');
        
        // 2. Delete all quests
        const questsQuery = query(collection(db, 'quests'), where('userId', '==', user.uid));
        const questsSnapshot = await getDocs(questsQuery);
        const questDeletions = questsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(questDeletions);
        console.log(`✅ Deleted ${questsSnapshot.size} quests`);
        
        // 3. Delete all statistics
        const statisticsQuery = query(collection(db, 'statistics'), where('userId', '==', user.uid));
        const statisticsSnapshot = await getDocs(statisticsQuery);
        const statisticsDeletions = statisticsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(statisticsDeletions);
        console.log(`✅ Deleted ${statisticsSnapshot.size} statistics records`);
        
        // 4. Delete all programs
        const programsQuery = query(collection(db, 'programs'), where('userId', '==', user.uid));
        const programsSnapshot = await getDocs(programsQuery);
        const programDeletions = programsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(programDeletions);
        console.log(`✅ Deleted ${programsSnapshot.size} programs`);
        
        // 5. Delete all passive skills
        const passiveSkillsQuery = query(collection(db, 'passive_skills'), where('userId', '==', user.uid));
        const passiveSkillsSnapshot = await getDocs(passiveSkillsQuery);
        const passiveSkillDeletions = passiveSkillsSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(passiveSkillDeletions);
        console.log(`✅ Deleted ${passiveSkillsSnapshot.size} passive skills`);
        
        // 6. Delete achievements (document ID = userId)
        const achievementsDocRef = doc(db, 'achievements', user.uid);
        try {
          await deleteDoc(achievementsDocRef);
          console.log('✅ Achievements deleted');
        } catch (achievementsError: any) {
          // Achievements might not exist for all users
          if (achievementsError.code !== 'not-found' && achievementsError.code !== 'permission-denied') {
            console.warn('⚠️ Failed to delete achievements:', achievementsError);
          }
        }
        
        // Note: We don't delete _abuse_records or email queue here because:
        // - _abuse_records is a protected admin-only collection
        // - emails queue is managed by Firebase extensions and auto-processes
        // These will be handled by server-side cleanup if needed
        
        console.log('✅ All user data deleted from Firestore');
      } catch (firestoreError) {
        console.error('⚠️ Failed to delete some user data from Firestore:', firestoreError);
        // Continue with account deletion even if some Firestore deletions fail
        throw new Error('Failed to delete all user data. Please try again or contact support.');
      }
      
      // Delete the user account from Firebase Authentication
      await deleteUser(user);
      console.log('✅ User account deleted from Firebase Authentication');
    } catch (error: any) {
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        throw new Error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/requires-recent-login') {
        throw new Error('Session expired. Please sign out and sign in again before deleting your account.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Please try again later.');
      }
      throw error;
    }
  },

  // Get current user
  getCurrentUser: (): User | null => {
    return auth.currentUser;
  },

  // Get user-friendly error messages
  getErrorMessage: (code: string): string => {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Please sign in instead.';
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed.';
      default:
        return 'An error occurred. Please try again.';
    }
  }
};

